import { RegionAuditInterface } from '../interfaces/region-audit.interface';
import { CommonEntityDto } from '../../common/dto/common-entity.dto';
import {
  IsBoolean,
  IsEnum,
  IsNumber,
  IsOptional,
  IsString,
  IsUUID,
  Max,
  MaxLength,
  Min,
} from 'class-validator';
import { Exclude, Expose, Type } from 'class-transformer';
import { ApiProperty, ApiPropertyOptional } from '@nestjs/swagger';
import { AuditActionTypeEnum } from '../../common/enums/audit-action-type.enum';
import { PhaseStructureTypeEnum } from '../../phase/enums/phase-structure-type.enum';
import { UserInterface } from '../../user/interfaces/user.interface';
import { UserDto } from '../../user/dto/user.dto';
import {
  REGION_COST_PER_BOARD_MIN,
  REGION_COST_PER_SQFT_MIN,
  REGION_PAYMENT_RATE_MAX,
  REGION_PAYMENT_RATE_MIN,
} from '../../region/constants/region-validation.constants';

@Exclude()
export class RegionAuditDto
  extends CommonEntityDto
  implements RegionAuditInterface
{
  @ApiProperty({
    format: 'uuid',
    description: 'Id of the region this audit record belongs to.',
  })
  @IsUUID()
  @Expose()
  regionId!: string;

  @ApiProperty({
    type: 'enum',
    enum: AuditActionTypeEnum,
    title: 'Action Type',
    description: 'The type of action that created this audit record.',
  })
  @IsEnum(AuditActionTypeEnum)
  @Expose()
  actionType!: AuditActionTypeEnum;

  @ApiProperty({
    format: 'uuid',
    description: 'Id of the user who made this change.',
  })
  @IsUUID()
  @Expose()
  changedByUserId!: UserInterface['id'];

  @ApiPropertyOptional({
    type: () => UserDto,
    description: 'The user who made this change.',
  })
  @Type(() => UserDto)
  @Expose()
  changedByUser?: UserInterface;

  @ApiPropertyOptional({
    description: 'Optional reason for the change.',
  })
  @IsString()
  @IsOptional()
  @MaxLength(500)
  @Expose()
  changeReason?: string;

  @ApiProperty({
    description: 'The name of the Region',
  })
  @IsString()
  @Expose()
  name!: string;

  @ApiProperty({
    type: 'boolean',
    description: 'True if region is archived',
  })
  @IsBoolean()
  @Expose()
  archived!: boolean;

  @ApiProperty({
    title: 'Repairs Rate',
    description: 'Repairs Rate',
  })
  @IsNumber({ maxDecimalPlaces: 2 })
  @Min(REGION_PAYMENT_RATE_MIN)
  @Max(REGION_PAYMENT_RATE_MAX)
  @Expose()
  repairsRate!: number;

  @ApiProperty({
    title: 'Special Jobs Percentage',
    description: 'Special Jobs Percentage',
  })
  @IsNumber({ maxDecimalPlaces: 2 })
  @Expose()
  specialJobsPercentage!: number;

  @ApiProperty({
    title: 'Piecework Percentage',
    description: 'Piecework Percentage',
  })
  @IsNumber({ maxDecimalPlaces: 2 })
  @Expose()
  pieceworkPercentage!: number;

  @ApiProperty({
    type: 'boolean',
    default: false,
    title: 'Allow Payout Amount Editing',
    description:
      'Allow Supervisor to edit the contractor payout amount for Piecework/Special Jobs repairs',
  })
  @IsBoolean()
  @Expose()
  allowPayoutAmountEditing!: boolean;

  @ApiProperty({
    title: 'Interior Options Percentage',
    description: 'Interior Options Percentage',
  })
  @IsNumber({ maxDecimalPlaces: 2 })
  @Expose()
  interiorOptionsPercentage!: number | null;

  @ApiProperty({
    title: 'Exterior Options Percentage',
    description: 'Exterior Options Percentage',
  })
  @IsNumber({ maxDecimalPlaces: 2 })
  @Expose()
  exteriorOptionsPercentage!: number | null;

  @ApiProperty({
    title: 'Trim Option Percentage',
    description: 'Trim Option Percentage',
  })
  @IsNumber({ maxDecimalPlaces: 2 })
  @Expose()
  trimOptionPercentage!: number | null;

  @ApiPropertyOptional({
    title: 'Exterior One Percentage',
    description:
      'Exterior One percentage in effect when this record was written. Null for records written before the field existed.',
    nullable: true,
  })
  @IsOptional()
  @IsNumber({ maxDecimalPlaces: 2 })
  @Expose()
  exteriorOnePercentage!: number | null;

  @ApiPropertyOptional({
    title: 'Exterior Two Percentage',
    description:
      'Exterior Two percentage in effect when this record was written. Null for records written before the field existed.',
    nullable: true,
  })
  @IsOptional()
  @IsNumber({ maxDecimalPlaces: 2 })
  @Expose()
  exteriorTwoPercentage!: number | null;

  @ApiProperty({
    type: 'enum',
    enum: PhaseStructureTypeEnum,
    nullable: true,
    title: 'Phase Structure Type',
    description: 'The phase structure type of this region',
  })
  @IsEnum(PhaseStructureTypeEnum)
  @Expose()
  phaseStructureType!: PhaseStructureTypeEnum;

  @ApiProperty({
    title: 'Garage Cost Fixed',
    description: 'Garage Cost Fixed',
  })
  @IsNumber({ maxDecimalPlaces: 2 })
  @Min(REGION_COST_PER_BOARD_MIN)
  @Expose()
  garageCostFixed!: number | null;

  @ApiProperty({
    title: 'Hanging Cost Per Board',
    description: 'Hanging Cost Per Board',
  })
  @IsNumber({ maxDecimalPlaces: 2 })
  @Min(REGION_COST_PER_BOARD_MIN)
  @Expose()
  dwHangingCostPerBoard!: number | null;

  @ApiProperty({
    title: 'Finishing Cost Per Board',
    description: 'Finishing Cost Per Board',
  })
  @IsNumber({ maxDecimalPlaces: 2 })
  @Min(REGION_COST_PER_BOARD_MIN)
  @Expose()
  dwFinishingCostPerBoard!: number | null;

  @ApiProperty({
    title: 'Spraying Cost Per Board',
    description: 'Spraying Cost Per Board',
  })
  @IsNumber({ maxDecimalPlaces: 2 })
  @Min(REGION_COST_PER_BOARD_MIN)
  @Expose()
  dwSprayingCostPerBoard!: number | null;

  @ApiProperty({
    title: 'High Ceiling Per Board',
    description: 'High Ceiling Per Board',
  })
  @IsNumber({ maxDecimalPlaces: 2 })
  @Min(REGION_COST_PER_BOARD_MIN)
  @Expose()
  highCeilingPerBoard!: number | null;

  @ApiProperty({
    title: 'Screw Inspection Per Board',
    description: 'Screw Inspection Per Board',
  })
  @IsNumber({ maxDecimalPlaces: 2 })
  @Min(REGION_COST_PER_BOARD_MIN)
  @Expose()
  screwInspectionPerBoard!: number | null;

  @ApiProperty({
    title: 'DWP Hanging Cost Per Board',
    description: 'Drywall and Paint Hanging Cost Per Board',
  })
  @IsNumber({ maxDecimalPlaces: 2 })
  @Min(REGION_COST_PER_BOARD_MIN)
  @Expose()
  dwpHangingCostPerBoard!: number | null;

  @ApiProperty({
    title: 'DWP Finish Cost Per Board',
    description: 'Drywall and Paint Finish Cost Per Board',
  })
  @IsNumber({ maxDecimalPlaces: 2 })
  @Min(REGION_COST_PER_BOARD_MIN)
  @Expose()
  dwpFinishCostPerBoard!: number | null;

  @ApiProperty({
    title: 'DWP Prime Cost Per Sqft',
    description: 'Drywall and Paint Prime Cost Per Sqft',
  })
  @IsNumber({ maxDecimalPlaces: 2 })
  @Min(REGION_COST_PER_SQFT_MIN)
  @Expose()
  dwpPrimeCostPerSqft!: number | null;

  @ApiProperty({
    title: 'DWP Bump 1 Cost Per Board',
    description: 'Drywall and Paint Bump 1 Cost Per Board',
  })
  @IsNumber({ maxDecimalPlaces: 2 })
  @Min(REGION_COST_PER_BOARD_MIN)
  @Expose()
  dwpBump1CostPerBoard!: number | null;

  @ApiProperty({
    title: 'DWP Trim 1 Cost Per Sqft',
    description: 'Drywall and Paint Trim 1 Cost Per Sqft',
  })
  @IsNumber({ maxDecimalPlaces: 2 })
  @Min(REGION_COST_PER_SQFT_MIN)
  @Expose()
  dwpTrim1CostPerSqft!: number | null;

  @ApiProperty({
    title: 'DWP Interior Paint Cost Per Sqft',
    description: 'Drywall and Paint Interior Paint Cost Per Sqft',
  })
  @IsNumber({ maxDecimalPlaces: 2 })
  @Min(REGION_COST_PER_SQFT_MIN)
  @Expose()
  dwpInteriorPaintCostPerSqft!: number | null;

  @ApiProperty({
    title: 'DWP Bump 2 Cost Per Board',
    description: 'Drywall and Paint Bump 2 Cost Per Board',
  })
  @IsNumber({ maxDecimalPlaces: 2 })
  @Min(REGION_COST_PER_BOARD_MIN)
  @Expose()
  dwpBump2CostPerBoard!: number | null;

  @ApiProperty({
    title: 'DWP Trim 2 Cost Per Sqft',
    description: 'Drywall and Paint Trim 2 Cost Per Sqft',
  })
  @IsNumber({ maxDecimalPlaces: 2 })
  @Min(REGION_COST_PER_SQFT_MIN)
  @Expose()
  dwpTrim2CostPerSqft!: number | null;

  @ApiProperty({
    title: 'DWP Final Paint Cost Per Sqft',
    description: 'Drywall and Paint Final Paint Cost Per Sqft',
  })
  @IsNumber({ maxDecimalPlaces: 2 })
  @Min(REGION_COST_PER_SQFT_MIN)
  @Expose()
  dwpFinalPaintCostPerSqft!: number | null;

  @ApiProperty({
    title: 'DWP Bump 3 Cost Per Board',
    description: 'Drywall and Paint Bump 3 Cost Per Board',
  })
  @IsNumber({ maxDecimalPlaces: 2 })
  @Min(REGION_COST_PER_BOARD_MIN)
  @Expose()
  dwpBump3CostPerBoard!: number | null;

  @ApiProperty({
    title: 'DWP CM Bluetape Cost Per Sqft',
    description: 'Drywall and Paint CM Bluetape Cost Per Sqft',
  })
  @IsNumber({ maxDecimalPlaces: 2 })
  @Min(REGION_COST_PER_SQFT_MIN)
  @Expose()
  dwpCMBluetapeCostPerSqft!: number | null;

  @ApiProperty({
    title: 'DWP Final Bluetape Cost Per Sqft',
    description: 'Drywall and Paint Final Bluetape Cost Per Sqft',
  })
  @IsNumber({ maxDecimalPlaces: 2 })
  @Min(REGION_COST_PER_SQFT_MIN)
  @Expose()
  dwpFinalBluetapeCostPerSqft!: number | null;
}
